/**
 * useTaxExpenses Hook
 * 경비 관리 상태 및 액션
 */

import { useState, useCallback } from 'react';
import { TaxRepository } from '../repositories/TaxRepository';
import {
  TaxExpense,
  TaxCategory,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  CsvUploadResult,
} from '../models/Tax';

const taxRepository = new TaxRepository();

export const useTaxExpenses = () => {
  const [expenses, setExpenses] = useState<TaxExpense[]>([]);
  const [categories, setCategories] = useState<TaxCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await taxRepository.getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('카테고리 로드 실패:', err);
    }
  }, []);

  const fetchExpenses = useCallback(async (params: {
    year?: number;
    month?: number;
    category?: string;
    search?: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const data = await taxRepository.getExpenses(params);
      setExpenses(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '경비 조회 실패';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const createExpense = useCallback(async (data: CreateExpenseRequest): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.createExpense(data);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '경비 추가 실패';
      setError(msg);
      return false;
    }
  }, []);

  const updateExpense = useCallback(async (id: string, data: UpdateExpenseRequest): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.updateExpense(id, data);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '경비 수정 실패';
      setError(msg);
      return false;
    }
  }, []);

  const deleteExpense = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.deleteExpense(id);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '경비 삭제 실패';
      setError(msg);
      return false;
    }
  }, []);

  const bulkDelete = useCallback(async (ids: string[]): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.bulkDeleteExpenses(ids);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '일괄 삭제 실패';
      setError(msg);
      return false;
    }
  }, []);

  const bulkUpdateCategory = useCallback(async (ids: string[], categoryId: string): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.bulkUpdateCategory(ids, categoryId);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '카테고리 변경 실패';
      setError(msg);
      return false;
    }
  }, []);

  const uploadCsv = useCallback(async (file: File): Promise<CsvUploadResult | null> => {
    setError(null);
    try {
      return await taxRepository.uploadCsv(file);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'CSV 업로드 실패';
      setError(msg);
      return null;
    }
  }, []);

  const exportCsv = useCallback(async (year?: number, month?: number) => {
    try {
      const blob = await taxRepository.exportCsv(year, month);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NTD_경비_${year || 'all'}_${month || 'all'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'CSV 내보내기 실패';
      setError(msg);
    }
  }, []);

  // 요약 통계
  const summary = {
    totalAmount: expenses.reduce((sum, e) => sum + e.amount, 0),
    totalVat: expenses.reduce((sum, e) => sum + e.vat, 0),
    totalSum: expenses.reduce((sum, e) => sum + e.total, 0),
    count: expenses.length,
  };

  return {
    expenses,
    categories,
    loading,
    error,
    summary,
    fetchExpenses,
    fetchCategories,
    createExpense,
    updateExpense,
    deleteExpense,
    bulkDelete,
    bulkUpdateCategory,
    uploadCsv,
    exportCsv,
  };
};
